import { useForm } from "react-hook-form";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import React, { createContext, memo, useContext, useEffect, useState } from "react";
import { PageLoader } from "../loader/PLoader";
import "./search.css";
import SearchResult from "./SearchResult";
import { useQuery } from "@tanstack/react-query";
import ms from "ms";
import Heading from "../heading/Heading";
import { useTranslation } from "react-i18next";
import useUser from "../../hooks/useUser";
import SnackBar from "../snackbarContent/SnackBar";
import { usePushToDataLayer } from "../../hooks/usePushToDataLayer";
import { GTM_EVENTS } from "../../utils/constants";

const SearchFormContext = createContext();

// True once the first search form of this document has mounted. The Navigation Timing
// type stays "reload" for the whole document lifetime, so it can only be trusted on that
// first mount — later mounts are in-app navigation (e.g. View detail -> back).
let documentEntryChecked = false;

// A hard reload (F5 / Ctrl+R / address-bar enter) should start from the origin state;
// in-app navigation keeps the stored search. Defaults to keeping it when the API is
// unavailable.
function isFreshPageLoad() {
	if (documentEntryChecked) return false;
	documentEntryChecked = true;
	try {
		const [nav] = performance.getEntriesByType?.("navigation") || [];
		return nav?.type === "reload";
	} catch {
		return false;
	}
}

export function useSearchFormContext() {
	const cntx = useContext(SearchFormContext);
	if (!cntx) {
		throw new Error("useSearchFormContext must be used within a SearchForm component");
	}
	return cntx;
}
function SearchForm({ 
	children, title, requiredInputs = 0, columns,
	criteriaFn, searchFn, downloadFns, 
	exportToolbar, getAllResult, action, customButtons, defaultValues
}) {
	const { t } = useTranslation();
	const { isFetching: criteriaFetching, data: criteriaData, isError: criteriaError } = useQuery({
		queryKey: [title , "criteria"],
		queryFn:criteriaFn,
		placeholderData: [],
		gcTime: ms("6h"),
		staleTime: ms("1d"),
	});
	const [searchData, setSearchData] = useState(() => {
		// Hard reload starts clean; in-app navigation restores the previous search so the
		// results are still there after View detail -> back.
		if (isFreshPageLoad()) {
			try { sessionStorage.removeItem(`searchData_${title}`); } catch { /* ignore */ }
			return {};
		}
		try {
			const stored = sessionStorage.getItem(`searchData_${title}`);
			return stored ? JSON.parse(stored) : {};
		} catch { return {}; }
	});

	// Persist searchData to sessionStorage whenever it changes
	const handleSetSearchData = (data) => {
		// Collapse identical successive updates. A single Search click can commit the
		// form value more than once (e.g. a scope switch re-seeds dropdown defaults),
		// which would otherwise set searchData to an equal-but-new object reference and
		// re-fire the result queries. Skipping equal payloads keeps it to one fetch.
		setSearchData((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
		try {
			if (Object.keys(data).length > 0) {
				sessionStorage.setItem(`searchData_${title}`, JSON.stringify(data));
			} else {
				sessionStorage.removeItem(`searchData_${title}`);
			}
		} catch { /* ignore storage errors */ }
	};

	return (
		<div className='container search__container'>
			<PageLoader show={criteriaFetching} />
			<div className='search__body'>
				<MainForm criteriaData={criteriaData} title={title} requiredInputs={requiredInputs} setSearchData={handleSetSearchData} action={action} customButtons={customButtons} defaultValues={defaultValues}>
					{children}
				</MainForm>
				<SearchResult title={title} columns={columns} 
					searchData={searchData} searchFn={searchFn} getAllResult={getAllResult}
				 	downloadFns={downloadFns} exportToolbar={exportToolbar}/>
			</div>
			{criteriaError && <SnackBar handleCloseSnackbar={() => {}} snackbar={{open: "true", message: "Something went wrong. Please try again later.", type: "error", errorType: "useQuery"}} />}
		</div>
	);
}

export const MainForm = memo(function MainForm({ children, criteriaData, requiredInputs, title, setSearchData, action, customButtons, defaultValues }) {
	const {
		register,
		control,
		handleSubmit,
		reset,
		getValues,
		setValue,
		formState: { errors },
		setError,
		clearErrors,
	} = useForm();
	const { t } = useTranslation();
	const pushToDL = usePushToDataLayer();

	// Restore form values from sessionStorage on mount (preserves filters after navigation).
	// On a hard reload SearchForm has already cleared the stored entry, so there is
	// nothing to restore.
	useEffect(() => {
		try {
			const stored = sessionStorage.getItem(`searchData_${title}`);
			if (stored) {
				const savedData = JSON.parse(stored);
				if (Object.keys(savedData).length > 0) {
					reset(savedData);
				}
			}
		} catch { /* ignore */ }
	}, [title, reset]);

	const onSubmit = (data) => {
		// Block submission if any date/range validation errors are present.
		// These errors are registered by SearchDateInput (invalid date, out of range,
		// or start date greater than end date) and must NOT trigger an API call.
		const blockingErrors = Object.keys(errors).filter(
			(key) => errors[key]?.type === "date" || errors[key]?.type === "range"
		);
		if (blockingErrors.length > 0) {
			return;
		}

		const validInputs = Object.keys(data).filter((v) => {
			if(typeof data[v] === "string")
				return data[v].trim().length !== 0
			if(Array.isArray(data[v]))
				return data[v].length !== 0
			
			return Boolean(data[v])
		}).length

		if (validInputs < requiredInputs) {
			Object.keys(data).forEach((v) => {
				if(typeof data[v] === "string") 
					setError(v, { message: t("Should be Provided") })
				else if(Array.isArray(data[v]) && data[v]?.length === 0) 
					setError(v, { message: t("Should be Selected") })
				else (!Boolean(data[v])) 
					setError(v, { message: t("Should be filled") });
			});

			return;
		}
		setSearchData(data);
	};
	return (
		<>
			<Heading title={title} action={action} />
			<SearchFormContext.Provider value={{ register, control, errors, criteriaData, setError, clearErrors }}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className='search__body--criteria'>{children}</div>
					<div className='btn-group'>
						<button
							className='btn btn-secondary'
							type='reset'
							onClick={() => {
								pushToDL(GTM_EVENTS.INTERACTIVE_CLICK, {
									button_text: 'Clear',
								})
								reset(defaultValues || {});
								setSearchData({});
								// Clear sessionStorage for this form
								try { sessionStorage.removeItem(`searchData_${title}`); } catch {}
							}}>
							<div className='btn-icon'>
								<ClearIcon />
								{t("Clear")}
							</div>
						</button>
						<button className='btn btn-primary' type='submit'>
							<div className='btn-icon'>
								<SearchIcon />
								{t("Search")}
							</div>
						</button>
						{customButtons && customButtons({ setSearchData, reset, getValues, setValue })}
					</div>
				</form>
			</SearchFormContext.Provider>
			{Object.keys(errors).length > 0 && requiredInputs > 0 ? (
				<div className='inputError'>
					{t("Please select")} {requiredInputs} {t("input(s)")}!
				</div>
			) : (
				<></>
			)}
		</>
	);
});
export default SearchForm;
